import { useEffect, useMemo, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  getPublicUserAction,
  getPublicUsersAction,
  unfollowUserAction,
  clearPublicUserProfileAction,
  createFollowRequestAction,
  getSentFollowRequestsAction,
} from "../redux/actions/userActions";
import { getSentProfileViewRequestsAction } from "../redux/actions/profileViewRequestActions";
import { clearUserDetailAction } from "../redux/actions/userDetailActions";
import PublicPost from "../components/profile/PublicPost";
import ProfileInfoCard from "../components/profile/ProfileInfoCard";
import MutualSkillsCard from "../components/profile/MutualSkillsCard";
import MutualCommunitiesCard from "../components/profile/MutualCommunitiesCard";
import UserProfile from "../components/profile/UserProfile";
import CommonLoading from "../components/loader/CommonLoading";
import { useAuthGate } from "../contexts/AuthGateContext";
import { useToast } from "../contexts/ToastContext";

const PublicProfile = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { openAuthGate } = useAuthGate();
  const { success: showSuccess, error: showError, info: showInfo } = useToast();

  const [followLoading, setFollowLoading] = useState(false);
  const [unfollowLoading, setUnfollowLoading] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [shouldClearProfile, setShouldClearProfile] = useState(false);
  const [isFetchingProfile, setIsFetchingProfile] = useState(false);
  const welcomeModalRef = useRef({ shown: false, url: "" });
  const justLoggedOutRef = useRef(false);

  const userData = useSelector((state) => state.auth?.userData);
  const userProfile = useSelector((state) => state.user?.publicUserProfile);
  const isUserFollowing = useSelector((state) => state.user?.isFollowing);
  const sentProfileViewRequests = useSelector((state) => state.user?.sentProfileViewRequests || []);
  const sentFollowRequests = useSelector((state) => state.user?.sentFollowRequests || []);
  const isModerator = useSelector(
    (state) => state.auth?.userData?.role === "moderator"
  );

  const isAuthenticated = useMemo(() => {
    const token = localStorage.getItem("profile");
    const accessToken = JSON.parse(token || "null")?.accessToken;
    return !!userData && !!accessToken;
  }, [userData]);

  const previousAuthenticatedRef = useRef(isAuthenticated);

  // Extract profileUrl or userId from URL
  const pathSegments = location.pathname.split("/").filter(Boolean);
  const profileUrlOrIdFromUrl = pathSegments[pathSegments.length - 1];
  const routePrefix = pathSegments[pathSegments.length - 2]; // 'post' or 'profile'
  
  // Check if URL param is an ObjectId (user ID)
  const isObjectId = /^[0-9a-fA-F]{24}$/.test(profileUrlOrIdFromUrl);
  
  // Determine view mode based on route
  // /post/:username = card view (posts + sidebar)
  // /profile/:username = full profile view
  const isFullProfileRoute = routePrefix === 'profile';
  
  // Set showFullProfile based on route - set immediately, not in useEffect
  // This ensures we don't show the intermediate ProfileInfoCard view when navigating from skills circle
  const showFullProfile = isFullProfileRoute;

  // Clear user detail data when profile URL changes to a different user
  // Use a ref to track the previous URL to only clear when user actually changes
  const previousProfileUrlRef = useRef(null);
  useEffect(() => {
    if (profileUrlOrIdFromUrl && previousProfileUrlRef.current !== profileUrlOrIdFromUrl) {
      // Clear user detail data when navigating to a different profile
      dispatch(clearUserDetailAction());
      previousProfileUrlRef.current = profileUrlOrIdFromUrl;
    }
  }, [profileUrlOrIdFromUrl, dispatch]);

  // On mount and when URL changes, check if loaded profile matches URL and clear if not
  // This handles the case when directly entering a URL with stale Redux data
  // Note: Only clear if we're navigating to a completely different user (different ID)
  // Don't clear for minor URL mismatches (case sensitivity, etc.) - let the profile render
  // IMPORTANT: Don't run this check while fetching - wait for fetch to complete
  useEffect(() => {
    // Skip if we're currently fetching - don't interfere with data loading
    if (isFetchingProfile) {
      return;
    }
    
    if (!profileUrlOrIdFromUrl || isRedirecting) {
      setShouldClearProfile(false);
      return;
    }
    
    const currentProfileId = userProfile?._id?.toString();
    if (currentProfileId) {
      // Only clear if it's a different user ID (not just URL format mismatch)
      const isDifferentUser = isObjectId && currentProfileId !== profileUrlOrIdFromUrl;
      
      // If it's clearly a different user (different ID), mark for clearing
      if (isDifferentUser) {
        setShouldClearProfile(true);
      } else {
        // Don't clear for URL format mismatches - allow profile to render
        setShouldClearProfile(false);
      }
    } else {
      setShouldClearProfile(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileUrlOrIdFromUrl, isRedirecting, userProfile?._id, userProfile?.profileUrl, isFetchingProfile]);

  // Clear profile when mismatch is detected
  // Only clear if it's actually a different user (not just URL format difference)
  useEffect(() => {
    if (shouldClearProfile) {
      // Double-check: only clear if it's truly a different user ID
      const currentProfileId = userProfile?._id?.toString();
      const isDifferentUser = isObjectId && currentProfileId && profileUrlOrIdFromUrl !== currentProfileId;
      
      if (isDifferentUser) {
        dispatch(clearPublicUserProfileAction());
        dispatch(clearUserDetailAction());
        lastFetchedUrlRef.current = null;
        currentProfileIdRef.current = null;
      }
      setShouldClearProfile(false);
    }
  }, [shouldClearProfile, dispatch, userProfile?._id, profileUrlOrIdFromUrl, isObjectId]);

  // Clear old profile data when navigating to a different user
  // Only clear if it's clearly a different user (different ID), not just URL format mismatch
  // IMPORTANT: Don't clear if we're currently fetching (isFetchingProfile) - wait for fetch to complete
  // Also don't clear immediately after fetching (justFetchedRef) to prevent race conditions
  useEffect(() => {
    // Skip if we're currently fetching or just fetched - don't clear while data is being loaded
    if (isFetchingProfile || justFetchedRef.current) {
      return;
    }
    
    const currentProfileId = userProfile?._id?.toString();
    
    if (currentProfileId && !isRedirecting && isObjectId) {
      // Only clear if URL is an ObjectId and it's different from current profile ID
      // This ensures we only clear when navigating to a completely different user
      if (profileUrlOrIdFromUrl !== currentProfileId) {
        // Reset the fetch tracking ref when clearing
        lastFetchedUrlRef.current = null;
        currentProfileIdRef.current = null;
        dispatch(clearPublicUserProfileAction());
        // Also clear user detail data to prevent showing old profile data
        dispatch(clearUserDetailAction());
      }
    }
    // Don't clear userDetailAction when no profile - this was causing issues
    // The profile fetch will handle loading states
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileUrlOrIdFromUrl, dispatch, isRedirecting, isObjectId, isFetchingProfile, userProfile?._id]);

  // Track the last fetched URL to prevent refetching
  const lastFetchedUrlRef = useRef(null);
  const currentProfileIdRef = useRef(null);
  const justFetchedRef = useRef(false);
  const previousLocationKeyRef = useRef(location.key);

  // Reset refs and redirecting state when user logs out (when isAuthenticated changes from true to false)
  useEffect(() => {
    if (!isAuthenticated && lastFetchedUrlRef.current) {
      // User logged out, reset refs and redirecting state to allow fresh fetch
      lastFetchedUrlRef.current = null;
      currentProfileIdRef.current = null;
      setIsRedirecting(false);
    }
  }, [isAuthenticated]);

  // Fetch user data - but skip if we're redirecting (to avoid double fetch)
  // Also avoid refetching when we already have the correct profile loaded
  // Note: Allow fetch even when redirecting if profile is not loaded (e.g., after logout)
  // When location.key changes (e.g. navigating to same profile from notification), clear cache so we refetch
  useEffect(() => {
    if ((isRedirecting && userProfile?._id) || !profileUrlOrIdFromUrl || isFetchingProfile) return;

    if (location.key !== previousLocationKeyRef.current) {
      previousLocationKeyRef.current = location.key;
      lastFetchedUrlRef.current = null;
    }

    // Update ref when userProfile changes (for comparison, not as dependency)
    if (userProfile?._id) {
      currentProfileIdRef.current = userProfile._id.toString();
    }

    // Skip if we already fetched for this URL AND profile is actually loaded
    if (lastFetchedUrlRef.current === profileUrlOrIdFromUrl) {
      // Double-check if the loaded profile matches
      if (userProfile && userProfile._id) {
        const profileUrl = userProfile.profileUrl || "";
        const profileId = userProfile._id.toString();
        const urlParamLower = (profileUrlOrIdFromUrl || "").toLowerCase();
        const profileUrlLower = profileUrl.toLowerCase();

        const urlMatchesProfile =
          (isObjectId && profileId === profileUrlOrIdFromUrl) ||
          (!isObjectId && profileUrlLower === urlParamLower) ||
          (!isObjectId && profileId === profileUrlOrIdFromUrl);

        if (urlMatchesProfile) {
          return;
        }
      } else {
        // Profile was cleared (e.g., after logout), reset ref to allow refetch
        lastFetchedUrlRef.current = null;
      }
    }

    const hasProfileLoaded = userProfile && userProfile._id;

    if (hasProfileLoaded) {
      const profileUrl = userProfile.profileUrl || "";
      const profileId = userProfile._id.toString();
      const urlParamLower = (profileUrlOrIdFromUrl || "").toLowerCase();
      const profileUrlLower = profileUrl.toLowerCase();

      const urlMatchesProfile =
        (isObjectId && profileId === profileUrlOrIdFromUrl) ||
        (!isObjectId && profileUrlLower === urlParamLower) ||
        (!isObjectId && profileId === profileUrlOrIdFromUrl);

      if (urlMatchesProfile) {
        lastFetchedUrlRef.current = profileUrlOrIdFromUrl;
        return;
      }
    }

    // Mark that we're fetching for this URL
    lastFetchedUrlRef.current = profileUrlOrIdFromUrl;
    setIsFetchingProfile(true);
    justFetchedRef.current = true;
    
    dispatch(getPublicUserAction(profileUrlOrIdFromUrl))
      .then(() => {
        // Reset the flag after a short delay to allow Redux state to update
        setTimeout(() => {
          justFetchedRef.current = false;
        }, 200);
      })
      .catch((error) => {
        console.error('getPublicUserAction error:', error);
        justFetchedRef.current = false;
      })
      .finally(() => {
        setIsFetchingProfile(false);
      });
    // Fetch sent requests to update button state in ProfileInfoCard and UserProfile
    // Only fetch if authenticated (these are user-specific requests)
    if (isAuthenticated) {
      dispatch(getSentProfileViewRequestsAction());
      dispatch(getSentFollowRequestsAction(null, 25, 0));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, profileUrlOrIdFromUrl, isRedirecting, isObjectId, location.key]);

  // Update URL to match the fetched user's profileUrl (handles both ObjectId and profileUrl mismatches)
  // But only if the current profile actually matches the URL parameter
  useEffect(() => {
    if (userProfile && userProfile._id && !isRedirecting) {
      const profileUrl = userProfile.profileUrl || "";
      const profileId = userProfile._id.toString();
      const urlParamLower = (profileUrlOrIdFromUrl || "").toLowerCase();
      const profileUrlLower = profileUrl.toLowerCase();
      
      // Check if the current profile matches the URL (by ID or profileUrl)
      const urlMatchesProfile = 
        (isObjectId && profileId === profileUrlOrIdFromUrl) ||
        (!isObjectId && profileUrlLower === urlParamLower) ||
        (!isObjectId && profileId === profileUrlOrIdFromUrl);
      
      // Only update URL if:
      // 1. The profile matches the URL (meaning we fetched the correct user)
      // 2. We have a valid profileUrl
      // 3. The profileUrl doesn't match the current URL (needs update)
      if (urlMatchesProfile && profileUrl && profileUrl !== profileUrlOrIdFromUrl) {
        setIsRedirecting(true);
        // Navigate to the correct profileUrl URL based on current route
        // If on /profile/ route, keep it; otherwise use /post/ route for card view
        const targetRoute = isFullProfileRoute ? 'profile' : 'post';
        navigate(`/${targetRoute}/${profileUrl}`, { replace: true });
        return;
      }
    }
    
    // Reset redirecting state if URL matches or if we don't have a profile yet
    if (isRedirecting && (!userProfile || (userProfile && userProfile._id && (() => {
      const profileUrl = userProfile.profileUrl || "";
      const profileId = userProfile._id.toString();
      const urlParamLower = (profileUrlOrIdFromUrl || "").toLowerCase();
      const profileUrlLower = profileUrl.toLowerCase();
      const urlMatchesProfile = 
        (isObjectId && profileId === profileUrlOrIdFromUrl) ||
        (!isObjectId && profileUrlLower === urlParamLower) ||
        (!isObjectId && profileId === profileUrlOrIdFromUrl);
      return urlMatchesProfile && profileUrlLower === urlParamLower;
    })()))) {
      setIsRedirecting(false);
    }
  }, [userProfile, profileUrlOrIdFromUrl, navigate, isRedirecting, isObjectId, isFullProfileRoute]);

  useEffect(() => {
    // Check if viewing own profile
    if (userData && userProfile?._id === userData?._id) {
      navigate("/profile", { replace: true });
    }
  }, [userProfile, userData, navigate]);

  // Track authentication state changes to detect logout
  useEffect(() => {
    // Detect if user just logged out (was authenticated, now not)
    if (previousAuthenticatedRef.current === true && isAuthenticated === false) {
      justLoggedOutRef.current = true;
      // Reset after a delay to allow future visits to show modal
      setTimeout(() => {
        justLoggedOutRef.current = false;
      }, 3000);
    }
    previousAuthenticatedRef.current = isAuthenticated;
  }, [isAuthenticated]);

  // Show welcome modal when logged-out user visits profile page
  // But don't show if user just logged out
  useEffect(() => {
    const currentUrl = location.pathname;
    
    // Reset flag when navigating to a different profile
    if (welcomeModalRef.current.url !== currentUrl) {
      welcomeModalRef.current.shown = false;
      welcomeModalRef.current.url = currentUrl;
      // Reset logout flag when navigating to different profile
      justLoggedOutRef.current = false;
    }

    // Don't show modal if user just logged out
    if (justLoggedOutRef.current) {
      return;
    }

    // Only show once per page load, when profile is loaded, user is not authenticated
    if (
      !isAuthenticated &&
      userProfile?._id &&
      !welcomeModalRef.current.shown &&
      !isRedirecting
    ) {
      welcomeModalRef.current.shown = true;
      // Small delay to ensure page is rendered
      setTimeout(() => {
        openAuthGate({
          reason: "view_profile",
          redirectTo: location.pathname,
        });
      }, 500);
    }
  }, [
    isAuthenticated,
    userProfile?._id,
    isRedirecting,
    location.pathname,
    openAuthGate,
  ]);

  // Always refresh sent follow requests and profile view requests when viewing a profile
  // So that after e.g. "follow request accepted" we get fresh state when landing on the profile
  useEffect(() => {
    if (!profileUrlOrIdFromUrl || !isAuthenticated) return;
    dispatch(getSentFollowRequestsAction(null, 25, 0));
    dispatch(getSentProfileViewRequestsAction());
  }, [profileUrlOrIdFromUrl, isAuthenticated, dispatch]);

  // Refetch profile when sentFollowRequests changes and the current user is no longer in the list
  // (e.g. our follow request was accepted elsewhere) so the button updates from Requested/Follow to Following
  const previousSentFollowRequestsRef = useRef([]);
  useEffect(() => {
    if (!isAuthenticated || !userProfile?._id || isFetchingProfile) {
      previousSentFollowRequestsRef.current = sentFollowRequests;
      return;
    }
    const profileUserId = userProfile._id.toString();
    const hadPendingRequest = previousSentFollowRequestsRef.current.some(
      (req) => (req.recipient?._id || req.recipient)?.toString() === profileUserId
    );
    const hasPendingRequest = sentFollowRequests.some(
      (req) => (req.recipient?._id || req.recipient)?.toString() === profileUserId
    );
    previousSentFollowRequestsRef.current = sentFollowRequests;
    if (hadPendingRequest && !hasPendingRequest) {
      lastFetchedUrlRef.current = null;
      setIsFetchingProfile(true);
      dispatch(getPublicUserAction(profileUrlOrIdFromUrl))
        .finally(() => setIsFetchingProfile(false));
    }
  }, [sentFollowRequests, isAuthenticated, userProfile?._id, profileUrlOrIdFromUrl, dispatch, isFetchingProfile]);

  // Refetch profile when sentProfileViewRequests changes and we have an accepted request
  // This handles the case when a request is approved and we need to update the profile access status
  const previousSentRequestsRef = useRef([]);
  const previousAccessDeniedRef = useRef(null);
  useEffect(() => {
    if (!isAuthenticated || !profileUrlOrIdFromUrl || !userProfile?._id) {
      previousSentRequestsRef.current = sentProfileViewRequests;
      previousAccessDeniedRef.current = userProfile?.accessDenied;
      return;
    }
    
    // Check if there's an accepted request for the current profile
    const profileUserId = userProfile._id.toString();
    const hasAcceptedRequest = sentProfileViewRequests.some((request) => {
      const profileOwnerId = request.profileOwner?._id || request.profileOwner;
      const matches = profileOwnerId && String(profileOwnerId) === String(profileUserId);
      const isAccepted = request.status?.toLowerCase() === "accepted";
      const isNotExpired = !(request.accessType === "limited" && 
                            request.expiryDate && 
                            new Date().getTime() >= new Date(request.expiryDate).getTime());
      return matches && isAccepted && isNotExpired;
    });
    
    // Check if sent requests actually changed (to avoid unnecessary refetches)
    const requestsChanged = JSON.stringify(previousSentRequestsRef.current) !== JSON.stringify(sentProfileViewRequests);
    const accessDeniedChanged = previousAccessDeniedRef.current !== userProfile?.accessDenied;
    
    // If we have an accepted request but profile still shows accessDenied, refetch
    // Also refetch if requests changed and we now have an accepted request
    if (hasAcceptedRequest && userProfile?.accessDenied === true && (requestsChanged || accessDeniedChanged) && !isFetchingProfile) {
      // Clear the last fetched URL to force a refetch
      lastFetchedUrlRef.current = null;
      setIsFetchingProfile(true);
      dispatch(getPublicUserAction(profileUrlOrIdFromUrl)).finally(() => {
        setIsFetchingProfile(false);
      });
    }
    
    // Update refs to track changes
    previousSentRequestsRef.current = sentProfileViewRequests;
    previousAccessDeniedRef.current = userProfile?.accessDenied;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sentProfileViewRequests, isAuthenticated, profileUrlOrIdFromUrl]);

  // Use profileUrl from API, fallback to URL param
  const profileUrl = userProfile?.profileUrl || profileUrlOrIdFromUrl;

  // Show loading if we're redirecting from ID to username
  if (isRedirecting && isObjectId) {
    return (
      <div className="flex justify-center items-center py-8">
        <CommonLoading />
      </div>
    );
  }

  const handleUnfollow = async (userId) => {
    if (!isAuthenticated) {
      openAuthGate({ reason: "follow", redirectTo: location.pathname });
      return;
    }
    setUnfollowLoading(true);
    try {
      const result = await dispatch(unfollowUserAction(userId));
      if (result?.success) {
        showSuccess("Unfollowed successfully");
        // Refresh the current profile to update button state and counts
        await dispatch(getPublicUserAction(profileUrlOrIdFromUrl));
        await dispatch(getPublicUsersAction());
      } else {
        showError(result?.error || "Failed to unfollow user");
      }
    } catch (error) {
      console.error("Error unfollowing user:", error);
      showError("Error unfollowing user");
    } finally {
      setUnfollowLoading(false);
    }
  };

  const handleFollow = async (userId) => {
    if (!isAuthenticated) {
      openAuthGate({ reason: "follow", redirectTo: location.pathname });
      return;
    }
    setFollowLoading(true);
    try {
      // Create a follow request instead of directly following
      const result = await dispatch(createFollowRequestAction(userId));
      if (result?.success) {
        showSuccess("Follow request sent successfully");
        // Refresh sent follow requests to show the new request
        await dispatch(getSentFollowRequestsAction(null, 25, 0));
        // Refresh the current profile to update button state and counts
        await dispatch(getPublicUserAction(profileUrlOrIdFromUrl));
        await dispatch(getPublicUsersAction());
      } else {
        // If creating request fails, check the error type
        if (result?.error && (result.error.includes("already pending") || result.error.includes("Follow request already pending"))) {
          // Show info message for pending request
          showInfo("Follow request already pending");
          // Refresh sent follow requests to update UI state
          await dispatch(getSentFollowRequestsAction(null, 25, 0));
          // Refresh profile to ensure UI is in sync
          await dispatch(getPublicUserAction(profileUrlOrIdFromUrl));
        } else if (result?.error && result.error.includes("only after they follow")) {
          // Show error message
          showError(result.error || "You can follow this user only after they follow you");
          // Refresh profile to update UI
          await dispatch(getPublicUserAction(profileUrlOrIdFromUrl));
        } else {
          // Show error message
          showError(result?.error || "Failed to send follow request");
          // Still refresh profile to ensure UI is in sync
          await dispatch(getPublicUserAction(profileUrlOrIdFromUrl));
        }
      }
    } catch (error) {
      console.error("Error creating follow request:", error);
      showError("Error sending follow request");
      // Refresh sent follow requests in case of error to ensure UI is in sync
      await dispatch(getSentFollowRequestsAction(null, 25, 0));
      // Refresh profile to ensure UI is in sync
      await dispatch(getPublicUserAction(profileUrlOrIdFromUrl));
    } finally {
      setFollowLoading(false);
    }
  };

  // Extract profileUserId and username before access check
  // First, check if we have a profile loaded
  // Check if userProfile exists and has _id (not just empty object)
  const hasProfileLoaded = !!(userProfile && Object.keys(userProfile).length > 0 && userProfile._id);
  
  
  // Show loading only if we're clearing profile or if no profile loaded at all
  // Don't block on URL mismatch - if we have profile data, show it (URL matching happens later)
  // Also show loading if we're currently fetching
  if (shouldClearProfile || !hasProfileLoaded || isFetchingProfile) {
    return (
      <div className="flex justify-center items-center py-8">
        <CommonLoading />
      </div>
    );
  }

  // Extract profileUserId and username before access check
  const profileUserId = userProfile._id;

  const {
    name,
    mystId,
    accessDenied = false, // Default to false if not set
    reason: accessReason,
    message: accessMessage,
  } = userProfile;


  // Prefer global follow state if available (keeps in sync after follow/unfollow),
  // otherwise fall back to the value returned with the profile payload.
  const effectiveIsFollowing =
    typeof isUserFollowing === "boolean"
      ? isUserFollowing
      : !!userProfile.isFollowing;

  // Format userProfile data for UserProfile component
  const formattedUserData = {
    name: userProfile.name || '',
    email: userProfile.email || '',
    avatar: userProfile.avatar || '',
    profilePictureFileName: userProfile.profilePictureFileName || '',
    profilePictureFileId: userProfile.profilePictureFileId || '',
    phone: userProfile.mobileNumber || '',
    location: userProfile.location || '',
    mobileNumber: userProfile.mobileNumber || '',
    city: userProfile.city || '',
    country: userProfile.country || '',
    dob: userProfile.dob ? (() => {
      const dobNumber = Number(userProfile.dob);
      if (isNaN(dobNumber)) return userProfile.dob;
      const date = new Date(dobNumber);
      return date.toLocaleDateString('en-GB');
    })() : '',
    gender: userProfile.gender || '',
    address1: userProfile.address1 || '',
    bio: userProfile.bio || '',
    summary: userProfile.summary || '',
    mystId: userProfile.mystId || '',
  };

  // If showFullProfile is true, show the full UserProfile component
  // The userId prop passed to UserProfile ensures it shows the PROFILE OWNER's data,
  // not the current viewer's data
  // Show profile structure even when access is denied (will show locks and limited info)
  if (showFullProfile) {
    // Pass the profile owner's mystId to ensure Skills/About/Employment show THEIR data
    // Show profile structure even when access is denied (will show locks)
    return (
      <div className="flex flex-col lg:flex-row gap-2 md:gap-3 max-w-7xl mx-auto px-2 md:px-6 lg:pl-0">
        {/* Main Content - UserProfile */}
        <div className="flex-1 w-full lg:max-w-6xl order-1 lg:order-2 min-w-0">
          <UserProfile
            userData={formattedUserData}
            userId={mystId || null} // This is the profile owner's mystId, ensures correct data is fetched
            profileUserId={profileUserId} // This is the profile owner's _id, used for fetching posts
            privacySettings={userProfile?.privacySettings}
            accessDenied={accessDenied}
            accessMessage={accessMessage}
            handleFollow={handleFollow}
            handleUnfollow={handleUnfollow}
            followLoading={followLoading}
            unfollowLoading={unfollowLoading}
            isFollowing={effectiveIsFollowing}
            isModerator={isModerator}
            isAuthenticated={isAuthenticated}
            onRequireAuth={(reason) => {
              openAuthGate({ reason: reason || "auth_required", redirectTo: location.pathname });
            }}
            userProfile={userProfile}
            sentFollowRequests={sentFollowRequests}
          />
        </div>

        {/* Right Sidebar - Mutual Cards */}
        {isAuthenticated && (
          <div className="hidden md:block lg:w-60 space-y-0 md:space-y-3 order-3 lg:order-3">
            <MutualSkillsCard userId={profileUserId} />
            <MutualCommunitiesCard userId={profileUserId} />
          </div>
        )}
      </div>
    );
  }

  // Default view: Show posts with sidebar
  return (
    <div className="flex flex-col lg:flex-row gap-2 md:gap-3 max-w-7xl mx-auto px-2 md:px-0 lg:pl-0">
      {/* Left Sidebar - Profile Info Card */}
      <div className="hidden md:block lg:w-60 space-y-2 md:space-y-3 order-2 lg:order-1">
        <ProfileInfoCard
          userProfile={userProfile}
          publicUserId={profileUserId}
          publicUsername={profileUrl}
          isFollowing={effectiveIsFollowing}
          isModerator={isModerator}
          followLoading={followLoading}
          unfollowLoading={unfollowLoading}
          handleFollow={handleFollow}
          handleUnfollow={handleUnfollow}
          accessDenied={accessDenied}
          accessReason={accessReason}
          accessMessage={accessMessage}
          isAuthenticated={isAuthenticated}
          onRequireAuth={(reason) => {
            openAuthGate({ reason: reason || "auth_required", redirectTo: location.pathname });
          }}
          onViewProfileClick={() => {
            const profileUrl = userProfile?.profileUrl || profileUrlOrIdFromUrl;
            if (profileUrl) {
              navigate(`/profile/${profileUrl}`, { replace: true });
            }
          }}
        />
      </div>

      {/* Center Section - Posts Only */}
      <div className="flex-1 w-full lg:max-w-6xl order-1 lg:order-2 min-w-0">
        <PublicPost publicUserId={profileUserId} userName={name} />
      </div>

      {/* Right Sidebar - Cards */}
      <div className="w-full md:w-auto lg:w-60 space-y-2 md:space-y-3 order-3 lg:order-3 lg:-mr-6">
        {/* Mutual cards require auth (avoid 401 spam when logged out) */}
        {isAuthenticated && (
          <>
            <MutualSkillsCard userId={profileUserId} />
            <MutualCommunitiesCard userId={profileUserId} />
          </>
        )}
      </div>
    </div>
  );
};

export default PublicProfile;
